import { Component, OnInit, Input, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Project, ModelType } from 'src/DataModels';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../auth.service';
import { AppUtilityService } from '../app-utility.service';
import { ProjectsService } from '../projects.service';
import { GenericDeleteConfirmationDialogComponent } from '../dialog/generic-delete-confirmation-dialog/generic-delete-confirmation-dialog.component';
import { CreateModelTypeDialogComponent } from '../dialog/create-model-type-dialog/create-model-type-dialog.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { CMSTableViewBase } from '../interfaces/cms-component-base';

@Component({
  providers: [ {provide: CMSTableViewBase, useExisting: ModelTypesTableViewComponent }],
  selector: 'app-model-types-table-view',
  templateUrl: './model-types-table-view.component.html',
  styleUrls: ['./model-types-table-view.component.css']
})
export class ModelTypesTableViewComponent extends CMSTableViewBase implements OnInit, AfterViewInit 
{
  @ViewChild(MatTable, {static: false}) table: MatTable<ModelType>
  @ViewChild('headerCheckbox', {read: MatCheckbox, static: false}) selectAllCheckbox: MatCheckbox;

  private clientColumns = ['Name', 'Description', 'Square Footage', 'Outdoor Square Footage', 'Price', 'Beds', 'Baths', 'Den Included', 'Edit'];
  private adminColumns = ['Checkbox', 'Name', 'Description', 'Square Footage', 'Outdoor Square Footage', 'Price', 'Beds', 'Baths', 'Den Included', 'Edit'];

  public modelTypesDataSource: MatTableDataSource<ModelType> = new MatTableDataSource<ModelType>();
  public selectedModelTypeIDs: Set<number> = new Set<number>();

  private _project: Project = null;

  @Input()
  set project(project: Project)
  {
    this._project = project;
  }

  get project()
  {
    return this._project;
  }

  private _modelID: number;

  @Input()
  set modelID(modelID: number)
  {
    this._modelID = modelID;
  }

  get modelID()
  {
    return this._modelID;
  }

  constructor(private dialog: MatDialog, public authService: AuthService, public appUtilityService: AppUtilityService, public projectsService: ProjectsService, private changeDetectorRef: ChangeDetectorRef) 
  {
    super();
  }

  ngOnInit() 
  {
  }

  ngAfterViewInit()
  {
    this.RenderTableRows();
  }

  public GetDisplayedColumns()
  {
    return this.authService.IsActiveUserAdmin() ? this.adminColumns : this.clientColumns;
  }

  public RenderTableRows()
  {
    this.modelTypesDataSource.data = this.GetModelTypesIterator();
    this.table.renderRows();
    this.changeDetectorRef.detectChanges();
  }

  public GetModelTypesIterator(): Array<ModelType>
  {
    let modelTypesArray: Array<ModelType> = new Array<ModelType>();

    for (let modelType of this.project.model_types.values())
    {
      if (modelType.model_id == this.modelID)
      {
        modelTypesArray.push(modelType);
      }
    }

    modelTypesArray = modelTypesArray.sort((m1, m2) => 
    {
      let m1Name = m1.name.toLowerCase();
      let m2Name = m2.name.toLowerCase();

      if (m1Name < m2Name)
      {
        return -1;
      }
      else if (m1Name > m2Name)
      {
        return 1;
      }
      else
      {
        return 0;
      }
    });

    return modelTypesArray;
  }

  public OnSelectAllCheckboxChanged(checked: boolean)
  {
    if (checked)
    {
      for (let modelType of this.modelTypesDataSource.filteredData)
      {
        this.selectedModelTypeIDs.add(modelType.model_type_id);
      }
    }
    else
    {
      this.selectedModelTypeIDs.clear();
    }
  }

  public OnModelTypeCheckboxToggled(checked: boolean, modelTypeID: number)
  {
    if (checked == true)
    {
      this.selectedModelTypeIDs.add(modelTypeID);
    }
    else
    {
      this.selectedModelTypeIDs.delete(modelTypeID);
    }
  }

  public ShouldDisplayAddButton(): boolean 
  {
    if (this.authService.IsActiveUserAdmin())
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  public OnAddButtonPressed()
  {
    const dialogRef = this.dialog.open(CreateModelTypeDialogComponent, { width: '350px', height: '480px', data: { selectedProject: this.project, selectedModelID: this.modelID } });

    dialogRef.afterClosed().subscribe(result => 
    {
      if (result != null)
      {
        this.projectsService.CreateNewModelType(this.project.project_id, result).subscribe((newModelType: ModelType) => 
        {
          if (newModelType != null)
          {
            this.project.model_types.set(newModelType.model_type_id, newModelType);
            this.appUtilityService.StoreCMSData();
            this.RenderTableRows();
          }
        });
      }
    });
  }

  public ShouldDisplayEditButton(): boolean
  {
    if (this.authService.IsActiveUserAdmin() && this.authService.activeUserRole.can_edit_projects_data)
    {
      return true;
    }
    else if (!this.authService.IsActiveUserAdmin())
    {
      let projectRole = this.appUtilityService.GetActiveUserProjectRole(this.project)
      if (projectRole != null)
      {
        return projectRole.can_modify_pricing || projectRole.can_modify_unit_data;
      }

      return false;
    }

    return false;
  }

  public OnEditButtonPressed(modelType: ModelType)
  {
    const dialogRef = this.dialog.open(CreateModelTypeDialogComponent, { width: '350px', height: '480px', data: { selectedProject: this.project, selectedModelID: this.modelID, modelTypeToEdit: modelType } });

    dialogRef.afterClosed().subscribe(result => 
    {
      if (result != null)
      {
        this.projectsService.UpdateProjectModelType(this.project.project_id, modelType.model_type_id, result).subscribe((updatedModelType: ModelType) => 
        {
          if (updatedModelType != null)
          {
            this.project.model_types.set(updatedModelType.model_type_id, updatedModelType);
            this.appUtilityService.StoreCMSData();
            this.RenderTableRows();
          }
        });
      }
    });
  }

  public ShouldDisplayDeleteButton(): boolean 
  {
    if (this.authService.IsActiveUserAdmin() && this.selectedModelTypeIDs.size > 0)
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  public OnDeleteButtonPressed()
  {
    if (this.selectedModelTypeIDs.size > 0)
    {
      const dialogRef = this.dialog.open(GenericDeleteConfirmationDialogComponent, {width: '250px', height: '200px', data: { title: "Delete Model Types?" }});

      dialogRef.afterClosed().subscribe((result) => 
      {
        if (result == true)
        {
          this.projectsService.DeleteModelTypesFromProject(this.project.project_id, Array.from(this.selectedModelTypeIDs)).subscribe((updatedModelTypes: Map<number, ModelType>) => 
          {
            if (updatedModelTypes != null)
            {
              this.project.model_types = updatedModelTypes;
              this.appUtilityService.StoreCMSData();
              this.selectedModelTypeIDs.clear();
              
              this.RenderTableRows();
            }
          });
        }
      });
    }
  }

  HasSelectedRows(): boolean 
  {
    return this.selectedModelTypeIDs.size > 0;
  }

  GetSelectedRows(): Set<string | number> 
  {
    return this.selectedModelTypeIDs;
  }

  ClearSelectedRows(): void 
  {
    this.selectedModelTypeIDs.clear();
  }
}
